import React from 'react';

import i18n from 'i18n-js';
import { Typography, IconButton } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

import styles from './styles';
import { AdvancedPriceOption } from './components/AdvancedPriceOption';
import { PriceOptionForm } from './components/PriceOptionForm';
import { useAppSelector } from 'index';
import { actionCreators } from 'store/restaurantDetailsStore/actionCreators';
import { useDispatch } from 'react-redux';
import agent from 'api/agent';
import globalStore from 'store/globalStore';

export interface PricingLevelAccessState {
    isGenerated: boolean;
    accessCode: string;
    generatedAt: string | null;
    registrationUrl: string;
    qrCodeUrl: string;
}

export default function AdvancedPricing() {
    const classes = styles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [addNew, setAddNew] = React.useState(false);
    const [openedAccessPanelPriceLevelId, setOpenedAccessPanelPriceLevelId] =
        React.useState<string | null>(null);
    const [pricingLevelAccessStateById, setPricingLevelAccessStateById] = React.useState<
        Record<string, PricingLevelAccessState>
    >({});

    React.useEffect(() => {
        const nextState: Record<string, PricingLevelAccessState> = {};

        restaurant.pricingLevels.forEach((level) => {
            if (level.accessCode && level.qrCodeUrl) {
                nextState[level.id] = {
                    isGenerated: true,
                    accessCode: level.accessCode,
                    generatedAt: level.accessCodeGeneratedAt ?? null,
                    registrationUrl: level.qrCodeUrl,
                    qrCodeUrl: level.qrCodeUrl,
                };
            }
        });

        setPricingLevelAccessStateById((currentState) => ({
            ...nextState,
            ...currentState,
        }));
    }, [restaurant.pricingLevels]);

    function getQrCanvasId(priceLevelId: string) {
        return `pricing-level-qr-canvas-${priceLevelId}`;
    }

    function createNewPriceOption(name: string) {
        setAddNew(false);
        dispatch(actionCreators.createPricingLevel(restaurant.id, name));
    }

    function toggleAccessPanel(priceLevelId: string) {
        setOpenedAccessPanelPriceLevelId((currentId) =>
            currentId === priceLevelId ? null : priceLevelId
        );
    }

    function upsertGeneratedState(
        priceLevelId: string,
        accessCode: {
            id: string;
            name: string;
            accessCode: string;
            qrCodeUrl: string;
            accessCodeGeneratedAt?: string | null;
        }
    ) {
        setPricingLevelAccessStateById((currentState) => ({
            ...currentState,
            [priceLevelId]: {
                isGenerated: true,
                accessCode: accessCode.accessCode,
                generatedAt: accessCode.accessCodeGeneratedAt ?? null,
                registrationUrl: accessCode.qrCodeUrl,
                qrCodeUrl: accessCode.qrCodeUrl,
            },
        }));
    }

    async function generateForPriceLevel(priceLevelId: string) {
        try {
            const accessCode = await agent.Restaurants.CreatePricingLevelAccessCode(
                restaurant.id,
                priceLevelId,
                process.env.REACT_APP_WEBAPP_URL
            );

            if (accessCode) {
                upsertGeneratedState(priceLevelId, accessCode);
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.generatedSuccesfully')
                )(dispatch);
            }
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        }
    }

    async function regenerateForPriceLevel(priceLevelId: string) {
        try {
            const accessCode = await agent.Restaurants.CreatePricingLevelAccessCode(
                restaurant.id,
                priceLevelId,
                process.env.REACT_APP_WEBAPP_URL
            );

            if (accessCode) {
                upsertGeneratedState(priceLevelId, accessCode);
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.generatedSuccesfully')
                )(dispatch);
            }
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        }
    }

    async function downloadQrCode(priceLevelId: string, levelName: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Restaurants.GeneratePricingLevelAccessCodePDF(
                restaurant.id,
                priceLevelId,
                `pricing-level-${levelName}`
            );
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function clearPriceLevelAccessState(priceLevelId: string) {
        setPricingLevelAccessStateById((currentState) => {
            if (!currentState[priceLevelId]) {
                return currentState;
            }

            const nextState = { ...currentState };
            delete nextState[priceLevelId];
            return nextState;
        });

        setOpenedAccessPanelPriceLevelId((currentId) =>
            currentId === priceLevelId ? null : currentId
        );
    }

    return (
        <div className={classes.root}>
            <Typography variant="h5" align="left">
                {i18n.t('restaurant.advancedPricing')}
            </Typography>
            <div className={classes.pricesContainer}>
                <Typography variant="h6" align="left">
                    {i18n.t('restaurant.pricingLevels')}
                </Typography>
                <AdvancedPriceOption name={i18n.t('common.standard')} disabled />
                {restaurant.pricingLevels.map((level) => (
                    <AdvancedPriceOption
                        key={level.id}
                        name={level.name}
                        id={level.id}
                        accessState={pricingLevelAccessStateById[level.id]}
                        isAccessPanelOpen={openedAccessPanelPriceLevelId === level.id}
                        qrCanvasId={getQrCanvasId(level.id)}
                        onAccessPanelToggle={toggleAccessPanel}
                        onGenerateAccess={generateForPriceLevel}
                        onRegenerateAccess={regenerateForPriceLevel}
                        onDownloadQr={downloadQrCode}
                        onDeleteOption={clearPriceLevelAccessState}
                    />
                ))}
                {addNew ? (
                    <PriceOptionForm
                        action={createNewPriceOption}
                        cancel={() => setAddNew(false)}
                    />
                ) : (
                    <IconButton onClick={() => setAddNew(true)}>
                        <AddCircleOutlineIcon
                            color="primary"
                            fontSize="large"
                            style={{ cursor: 'pointer', marginTop: 15 }}
                        />
                    </IconButton>
                )}
            </div>
        </div>
    );
}
